import { useMutation, useQueryClient } from 'react-query';

import { FormActions, NotificationPosition } from 'coreOld/enums';
import { useHandleErrors, useInfiniteQuery } from 'coreOld/hooks';
import { actionLoadingMessage, actionMessage } from 'coreOld/utils';
import { usersService } from 'system/services';
import { ISystemUser } from 'system/types';

export const useGetUsers = (filters?) =>
  useInfiniteQuery<ISystemUser>(usersService.getAll, 'systemUsers', filters);

export const useUsersActions = () => ({
  ...useEditBulkUsers(),
  ...useBlockIpBulkUsers(),
  ...useUnblockIpBulkUsers(),
  ...useEditUserSettings(),
  ...useEditUserSettingsBulk(),
});

const useEditBulkUsers = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors(
    actionMessage('User'),
    actionLoadingMessage('user'),
    NotificationPosition.MODAL,
    ['systemUsers'],
    FormActions.UPDATE
  );

  const { mutateAsync } = useMutation((data) => usersService.editBulk(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['systemUsers', data?.data?.internalReference]);
    },
    ...errors,
  });

  const editBulk = (data) => mutateAsync(data);

  return { editBulk };
};

const useBlockIpBulkUsers = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors(
    actionMessage('User IP'),
    actionLoadingMessage('user IP'),
    NotificationPosition.MODAL,
    ['systemUsers'],
    FormActions.UPDATE
  );

  const { mutateAsync } = useMutation((data) => usersService.blockIpBulk(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['systemUsers', data?.data?.internalReference]);
    },
    ...errors,
  });

  const blockIpBulk = (data) => mutateAsync(data);

  return { blockIpBulk };
};

const useUnblockIpBulkUsers = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors(
    actionMessage('User IP'),
    actionLoadingMessage('user IP'),
    NotificationPosition.MODAL,
    ['systemUsers'],
    FormActions.UPDATE
  );

  const { mutateAsync } = useMutation((data) => usersService.unblockIpBulk(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['systemUsers', data?.data?.internalReference]);
    },
    ...errors,
  });

  const unblockIpBulk = (data) => mutateAsync(data);

  return { unblockIpBulk };
};

const useEditUserSettings = () => {
  const { errors } = useHandleErrors(
    actionMessage('User Setting'),
    actionLoadingMessage('user setting'),
    NotificationPosition.TOP,
    [],
    FormActions.UPDATE
  );

  const { mutateAsync, isLoading } = useMutation((data) => usersService.editUserSettings(data), {
    ...errors,
  });

  const editUserSettings = (data) => mutateAsync(data);

  return { editUserSettings, editUserSettingsIsLoading: isLoading };
};

const useEditUserSettingsBulk = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors(
    actionMessage('User Settings'),
    actionLoadingMessage('user settings'),
    NotificationPosition.MODAL,
    ['systemUsers'],
    FormActions.UPDATE
  );

  const { mutateAsync } = useMutation((data) => usersService.editUserSettingsBulk(data), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['systemUsers', data?.data?.internalReference]);
    },
    ...errors,
  });

  const editUserSettingsBulk = (data) => mutateAsync(data);

  return { editUserSettingsBulk };
};
